import React, { useEffect, useRef, useState } from "react";
import { StyleSheet, View, Animated } from "react-native";
import { actions as categoryActions } from "@reducers/categoriesReducer";
import analytics from "@services/analytics";
import debounce from "@utils/debounce";
import colors from "@constants/color";
import SearchBar from "@components/SearchBar";
import metrics from "@constants/metrics";
import SearchHome from "@assets/svg/SearchHome";
import CloseIcon from "@assets/svg/CloseIcon";
import t from "@constants/localization";
import testIds from "@constants/testIds";
import { useDispatch } from "react-redux";
import {
  getTrendingSearch,
  searchKeyword,
  searchRawKeyword,
} from "@services/search-api";
import useWindowDimensions from "react-native/Libraries/Utilities/useWindowDimensions";
import { STATE_STATUS } from "../../reducers/actionTypes";
import { DISCOVERY_FLOW_EVENTS } from "../../constants/analyticsEventsCT";
import SearchView from "./View";
import { L1FilterChip, L2FilterChip } from "./Components/FilterChips";
import FiltersStrip from "./Components/FiltersStrip";

const PATH = "screens/Search/index";

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: colors.darkBackground,
  },
});

const SearchBarContainer = (props) => {
  const searchref = useRef(null);
  const { searchInputPlaceholder, query, handleQueryChange } = props;
  return (
    <View
      style={{
        flexDirection: "row",
        paddingHorizontal: 10,
        paddingTop: 20,
        paddingBottom: 10,
        width: metrics.screenWidth,
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: colors.white,
        // elevation: 2,
      }}
    >
      <SearchBar
        ref={searchref}
        placeholder={searchInputPlaceholder || t(`${PATH}/searchProducts`)}
        query={query}
        handleQueryChange={handleQueryChange}
        shouldShowIcon={true}
        rightIcon={!query ? <SearchHome /> : <CloseIcon />}
        clearSearch={true}
        disabled
        onPressRightIcon={() => {
          searchref.current.clear();
          handleQueryChange("");
        }}
        backBtn
      />
    </View>
  );
};

const Search = (props) => {
  const { navigation, status } = props;
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [trendingData, setTrendingData] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await getTrendingSearch();
      setTrendingData(data.results);
    })();
  }, []);

  const fetchProducts = async (search_term = "", keywordClicked = false) => {
    if (search_term && search_term.length > 2) {
      setLoading(true);
      const searchMethod = keywordClicked ? searchKeyword : searchRawKeyword;
      searchMethod(search_term)
        .then(({ data }) => {
          updateL1AndL2FilterRes(data);
          setProducts(data.results.Active);
          setSearchResults(data.results.Active);
          setLoading(false);
          link_params_ref.current = search_term;
          setL1FilterSelected(null);
          setL2FilterSelected(null);
          const top3Results = data.results.Active.slice(0, 3)
            .map((result, index) => ({ [`r${index + 1}`]: result.sku_id }))
            .reduce(
              (acc, curr) => ({
                ...acc,
                [Object.keys(curr)[0]]: Object.values(curr)[0],
              }),
              {},
            );
          dispatch(categoryActions.updateRecentSearchData(data.recentSearches));
          analytics.trackEvent(DISCOVERY_FLOW_EVENTS.PRODUCT_SEARCHED, {
            search_term: searchTerm,
            keyword_clicked: keywordClicked,
            ...top3Results,
          });
        })
        .catch((e) => {
          setProducts([]);
          setLoading(false);
        });
    } else {
      setProducts([]);
    }
  };

  const link_params_ref = useRef(null);
  const isApiBeingCalled = useRef(false);
  const [l1filterSelected, setL1FilterSelected] = useState(null);
  const [l2filterSelected, setL2FilterSelected] = useState(null);

  const l1FilterSelectedRef = useRef({});
  const l2FilterSelectedRef = useRef({});
  const scrollY = useRef(new Animated.Value(1));
  const scrollYClamped = Animated.diffClamp(scrollY.current, 0, 100);
  const translateY = scrollYClamped.interpolate({
    inputRange: [0, 50],
    outputRange: [0, -100],
  });

  const { width } = useWindowDimensions();
  const l1FilterResults = useRef([]);
  const l2FilterResults = useRef([]);

  const updateL1AndL2FilterRes = (data, initialRender, fromFilter) => {
    if (
      data.results.l1_filters &&
      data.results.l1_filters.length > 1 &&
      initialRender
    ) {
      l1FilterResults.current = data.results.l1_filters;
      data.results.l1_filters.map((res) => {
        if (res?.is_pre_selected) {
          setL1FilterSelected(res);
          l1FilterSelectedRef.current = res;
        }
        return res;
      });
    }
    if (data.results.l1_filters && data.results.l1_filters.length > 0) {
      const resultsL1Filters = [...data.results.l1_filters];
      l1FilterResults.current = resultsL1Filters;
    }

    if ((data?.results?.l2_filters && fromFilter) || initialRender) {
      const resultsL2Filters = [...data.results.l2_filters];
      l2FilterResults.current = resultsL2Filters;
    }
  };

  const callSearchKeyword = async ({
    initialRender,
    fromFilter,
    filter1 = null,
    filter2 = null,
  }) => {
    let linkParams = link_params_ref.current;

    if (filter1) {
      Object.entries(filter1).forEach(([k, v]) => {
        const encodedValue = encodeURIComponent(v);
        linkParams += `&${k}=${encodedValue}`;
      });
    }
    if (filter2) {
      Object.entries(filter2).forEach(([k, v]) => {
        const encodedValue = encodeURIComponent(v);
        linkParams += `&${k}=${encodedValue}`;
      });
    }
    setLoading(true);
    await searchRawKeyword(linkParams)
      .then(({ data }) => {
        updateL1AndL2FilterRes(data, initialRender, fromFilter);
        setProducts(data.results.Active);
        setSearchResults(data.results.Active);
        setLoading(false);
        const top3Results = data.results.Active.slice(0, 3)
          .map((result, index) => ({
            [`r${index + 1}`]: result.sku_id,
          }))
          .reduce(
            (acc, curr) => ({
              ...acc,
              [Object.keys(curr)[0]]: Object.values(curr)[0],
            }),
            {},
          );
        dispatch(categoryActions.updateRecentSearchData(data.recentSearches));
        analytics.trackEvent(DISCOVERY_FLOW_EVENTS.PRODUCT_SEARCHED, {
          search_term: searchTerm,
          keyword_clicked: false,
          ...top3Results,
        });
      })
      .catch((err) => {
        isApiBeingCalled.current = false;
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onClickL1Filter = (filter, isSelected) => {
    if (isSelected) {
      setL2FilterSelected(null);
      l2FilterResults.current = null;
      setL1FilterSelected(null);
      l1FilterSelectedRef.current = null;

      callSearchKeyword({
        initialRender: false,
        fromFilter: true,
        filter1: null,
        filter2: null,
        fromL1Filter: true,
        fromL2Filter: false,
      });
    } else {
      setL1FilterSelected(filter);
      l1FilterSelectedRef.current = filter;

      setL2FilterSelected(null);
      l2FilterSelectedRef.current = null;
      // setL2FiltersResults(null);
      l2FilterResults.current = null;

      callSearchKeyword({
        initialRender: false,
        fromFilter: true,
        filter1: filter,
        filter2: null,
        fromL1Filter: true,
        fromL2Filter: false,
      });
    }
  };

  const onClickL2Filter = (filter, isSelected) => {
    if (isSelected) {
      setL2FilterSelected(null);
      l2FilterSelectedRef.current = null;
      callSearchKeyword({
        initialRender: false,
        fromFilter: true,
        filter1: l1filterSelected,
        filter2: null,
        fromL1Filter: false,
        fromL2Filter: true,
      });
    } else {
      setL2FilterSelected(filter);
      l2FilterSelectedRef.current = filter;
      callSearchKeyword({
        initialRender: false,
        fromFilter: true,
        filter1: l1filterSelected,
        filter2: filter,
        fromL1Filter: false,
        fromL2Filter: true,
      });
    }
  };

  const renderItemFilterL1 = ({ item: filterLevel }) => {
    const isSelected =
      l1filterSelected?.l1_filter_display_text ===
      filterLevel?.l1_filter_display_text;
    return (
      <L1FilterChip
        onClickL1Filter={onClickL1Filter}
        filterLevel={filterLevel}
        isSelected={isSelected}
      />
    );
  };

  const renderItemFilterL2 = ({ item: filterLevel }) => {
    const isSelected =
      l2filterSelected?.l2_filter_display_text ===
      filterLevel?.l2_filter_display_text;
    return (
      <L2FilterChip
        onClickL2Filter={onClickL2Filter}
        filterLevel={filterLevel}
        isSelected={isSelected}
      />
    );
  };

  const navigateToRequestProduct = () => {
    navigation &&
      navigation.navigate("AddProductRequest", {
        fromScreen: "Search",
      });
  };

  const handleQueryChange = async (keyword, keywordClicked) => {
    const doSearch = keyword.length > 2;
    setSearchTerm(keyword);
    setLoading(doSearch);
    link_params_ref.current = null;
    setL1FilterSelected(null);
    setL2FilterSelected(null);
    if (doSearch) {
      debounce(fetchProducts(searchTerm, keywordClicked), 300);
    } else {
      setSearchResults([]);
    }
  };

  const handleTrendingClicked = (trendingItem) => {
    setSearchTerm(trendingItem.text);
    setLoading(true);
    debounce(fetchProducts(trendingItem.link_params, true), 300);
  };
  return (
    <View style={styles.mainContainer} testID={testIds.Search_Bar}>
      <SearchBarContainer
        {...{
          query: searchTerm,
          handleQueryChange,
        }}
      />
      <SearchView
        {...{
          searchProducts: searchResults,
          setSelectedFilter: (filter) => {
            analytics.trackEvent(
              DISCOVERY_FLOW_EVENTS.PRODUCT_SEARCHED_FILTER_CLICKED,
              {
                search_term: searchTerm,
                selected_filter: filter?.filterText,
              },
            );
          },
          query: searchTerm,
          handleQueryChange,
          loading: status === STATE_STATUS.FETCHING ? true : loading,
          navigateTo: navigateToRequestProduct,
          navigation,
          trendingData,
          allProducts: products,
          handleTrendingClicked,
        }}
      >
        <FiltersStrip
          l1FilterResults={l1FilterResults}
          l2FilterResults={l2FilterResults}
          width={width}
          renderItemFilterL2={renderItemFilterL2}
          renderItemFilterL1={renderItemFilterL1}
          loading={loading}
          l1filterSelected={l1filterSelected}
          translateY={translateY}
        />
      </SearchView>
    </View>
  );
};

export default Search;
